import { z } from 'zod';

/** The two OnArk-owned funnel classes (see @onark/shared welcomeAboardFunnelClass). */
export const TRAINING_FUNNEL_CLASSES = [
  'onark_training',
  'wa_master_class',
] as const;

export type TrainingFunnelClass = (typeof TRAINING_FUNNEL_CLASSES)[number];

/** How the guest reached the check-in: a signed class link, or the typed form. */
export const TRAINING_CHECK_IN_VIAS = ['class_link', 'form'] as const;

// A first-touch attribution field: optional, and null-tolerant because the
// cookie parser hands back `null` for a param that was present but empty.
const touch = z.string().nullable().optional();

// The free OnArk training class as a LEAD SOURCE.
// Naming: domain.subject_verb, past tense — events describe what HAPPENED.
//
// A guest who arrives from an ad on /start/training, clicks into the class and
// checks in has handed over an email exactly the way the trial form's step 1
// does — and the form's step is counted as a lead in Meta and in PostHog
// (`leadCaptured`) while, until 2026-09-07, the check-in was counted nowhere.
// Meta reported one lead on a day four ad visitors gave an email, and the
// matrix scoreboard could not see the three who chose the class over the
// form. This event is the PostHog half of closing that: the check-in becomes
// countable as a lead, by cell, on the same person the landing identified.
//
// `leadCaptured` (the form's event) is deliberately left alone — this is a
// second lead KIND, not a second emitter of the first, and the two must stay
// separable so the door → form and door → class rates can be compared.
export const trainingSchemas = {
  /**
   * A guest checked into one of OnArk's own funnel classes, and that check-in
   * counts as a lead. Fires once per check-in from the class page, alongside
   * the browser Meta lead, and only for a NON-signed-in visitor: an account is
   * not a lead. Customer-church classes never emit this (the funnel-class gate
   * is server-computed; see the check-in page's `funnelClass` prop).
   *
   * The first-touch fields are the cell: which campaign / ad set / ad / door
   * the person came through. They ride on the event rather than relying on
   * PostHog's own initial-UTM person properties because the app's first-touch
   * cookie is what every other funnel count in this repo keys on.
   */
  'training.lead_captured': z.object({
    adId: touch,
    adsetId: touch,
    campaignId: touch,
    courseId: z.string(),
    // Meta/Google auto-append these to every ad click regardless of whether
    // the ad's URL-parameters field is set up correctly, so they're the most
    // reliable ad signal available — see docs/attribution-ad-url-convention.md.
    fbclid: touch,
    funnelClass: z.enum(TRAINING_FUNNEL_CLASSES),
    gclid: touch,
    landingPath: touch,
    utmCampaign: touch,
    utmContent: touch,
    utmMedium: touch,
    utmSource: touch,
    utmTerm: touch,
    via: z.enum(TRAINING_CHECK_IN_VIAS),
  }),
};
